import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { Keyboard, X } from 'lucide-react';
import type { Asset } from '@khadi/shared';
import { api, ApiError } from '../lib/api';
import { cn } from './ui';

/**
 * Codescanner.
 *
 * Nimmt entgegen, was auf einem Etikett stehen kann: die alte Snipe-URL,
 * eine reine ID, den Asset-Tag oder die Seriennummer. Die Auflösung macht
 * die API (/assets/lookup) — hier wird nur gelesen und weitergeleitet.
 *
 * `onResult` erlaubt die Wiederverwendung in der Inventur; ohne diesen
 * Rückruf springt der Scanner direkt auf die Geräteseite.
 */
export function ScannerSheet({
  onClose,
  onResult,
  title = 'Code scannen',
}: {
  onClose: () => void;
  onResult?: (code: string) => void | Promise<void>;
  title?: string;
}) {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [manual, setManual] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const handled = useRef(false);

  async function handleCode(code: string): Promise<void> {
    if (handled.current) return;
    handled.current = true;
    setBusy(true);
    setStatus(null);

    try {
      if (onResult) {
        await onResult(code);
        onClose();
        return;
      }

      const asset = await api<Asset>(`/assets/lookup/${encodeURIComponent(code)}`);
      onClose();
      void navigate({ to: '/geraete/$assetId', params: { assetId: String(asset.id) } });
    } catch (error) {
      handled.current = false;
      setStatus(
        error instanceof ApiError && error.status === 404
          ? 'Zu diesem Code ist kein Gerät hinterlegt.'
          : 'Der Code konnte nicht gelesen werden.',
      );
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (manual) return;

    const reader = new BrowserMultiFormatReader();
    let stop: (() => void) | undefined;
    let cancelled = false;

    void (async () => {
      try {
        const controls = await reader.decodeFromVideoDevice(
          undefined,
          videoRef.current ?? undefined,
          (result) => {
            if (result) void handleCode(result.getText());
          },
        );
        if (cancelled) controls.stop();
        else stop = () => controls.stop();
      } catch {
        // Kein Kamerazugriff (verweigert, kein Gerät, kein HTTPS) — dann eben tippen.
        setManual(true);
        setStatus('Kamera nicht verfügbar — Code bitte eingeben.');
      }
    })();

    return () => {
      cancelled = true;
      stop?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manual]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-[var(--color-khadi-forest-900)]/45 p-0 backdrop-blur-[2px] sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="animate-sheet-up safe-bottom w-full max-w-md rounded-t-[var(--radius-xl)] border border-[var(--color-line)] bg-[var(--color-bg-elevated)] p-5 shadow-[var(--shadow-lg)] sm:rounded-[var(--radius-xl)]">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-[family-name:var(--font-display)] text-2xl">{title}</h2>
          <button type="button" onClick={onClose} className="btn btn-quiet btn-sm" aria-label="Schließen">
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        {!manual && (
          <div className="relative mb-4 aspect-[4/3] overflow-hidden rounded-[var(--radius-lg)] bg-[var(--color-khadi-forest-900)]">
            <video ref={videoRef} className="h-full w-full object-cover" muted playsInline />

            {/* Sucherrahmen mit wanderndem Lesestrahl. */}
            <div aria-hidden className="pointer-events-none absolute inset-0 grid place-items-center">
              <div className="animate-breathe relative h-40 w-56 rounded-[var(--radius-md)] border-2 border-[var(--color-khadi-cream-50)]/80">
                <div className="animate-sweep absolute inset-x-0 h-0.5 bg-[var(--color-khadi-saffron-400)]" />
              </div>
            </div>
          </div>
        )}

        {status && (
          <p
            className={cn(
              'mb-3 rounded-[var(--radius-sm)] px-5 py-4 text-sm',
              'bg-[var(--color-khadi-clay-100)] text-[var(--color-khadi-clay-700)]',
            )}
          >
            {status}
          </p>
        )}

        {manual ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (manualCode.trim()) void handleCode(manualCode.trim());
            }}
            className="flex gap-2"
          >
            <input
              autoFocus
              value={manualCode}
              onChange={(event) => setManualCode(event.target.value)}
              placeholder="Nummer oder Seriennummer"
              className="field"
              aria-label="Code eingeben"
            />
            <button type="submit" className="btn btn-primary btn-md" disabled={busy}>
              Suchen
            </button>
          </form>
        ) : (
          <button
            type="button"
            onClick={() => setManual(true)}
            className="btn btn-ghost btn-md w-full"
          >
            <Keyboard className="h-4 w-4" aria-hidden />
            Stattdessen eintippen
          </button>
        )}

        <p className="mt-3 text-center text-sm text-[var(--color-fg3)]">
          Erkennt Inventarnummer, Seriennummer und die Codes der alten Etiketten.
        </p>
      </div>
    </div>
  );
}
